// OnboardProject.js
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styles from "./OnboardProject.module.scss";
import ProjectSelection from "./ProjectSelection";
import ConsultationType from "./ConsultationType";
import LocationSelection from "./LocationSelection";
import {
  getprojectlist,
  getprojectcenterlist,
  updateavailibility,
} from "../../actions/RPMAction";
import { CLEAR_RPM_ADMIN, UPDATE_USER } from "../../actions/type";
import { logoutAction } from "../../actions/LoginAction";
import { SvgIcons } from "../../assets/svg-icons";

const OnboardProject = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [projectList, setProjectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedConsultationType, setSelectedConsultationType] =
    useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isOnlineSelected, setIsOnlineSelected] = useState(null);
  const [centerList, setCenterList] = useState([]); // Initialize as empty array
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  let userData = null;
  try {
    const rawData = localStorage.getItem("userData");
    userData = rawData ? JSON.parse(rawData) : null;
  } catch (err) {
    console.error("Error parsing userData in OnboardProject", err);
  }
  const { profileName, profileId } = userData || {};

  // Redirect to signin if no userData
  useEffect(() => {
    if (!userData || !profileId) {
      console.log("No user data found, redirecting to signin");
      history.push("/signin");
      return;
    }
  }, []);

  // Handle cross-tab login - if userData changes in another tab, reload this page
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "userData" && e.newValue) {
        try {
          const newData = JSON.parse(e.newValue);
          // If a different user logs in, redirect to their project listing
          if (newData?.profileId && newData.profileId !== profileId) {
            console.log("Different user logged in from another tab, redirecting...");
            window.location.href = `/ccdc/projectlisting/${newData.profileId}`;
          }
        } catch (error) {
          console.error("Failed to parse userData from storage event:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [profileId]);

  useEffect(() => {
    loadProjects();
  }, []);
  useEffect(() => {
    const project = JSON.parse(localStorage.getItem("selectedProject"));
    if (project) {
      console.log("Selected project:", project);
      setSelectedProject(project);
    }
  }, []);

  const loadProjects = async () => {
    try {
      setError(null);
      const projects = await dispatch(getprojectlist(profileId));
      setProjectList(projects || []); // Ensure it's always an array
    } catch (error) {
      console.error("Error loading projects:", error);
      setError("Failed to load projects");
      setProjectList([]);
    }
  };

  const handleProjectSelect = async (project) => {
    if (!project) return;

    setSelectedProject(project);
    setSelectedLocation(null); // Reset location when project changes
    setLoading(true);
    setError(null);

    try {
      const centers = await dispatch(
        getprojectcenterlist(profileId, project.clinic_Id)
      );
      // Ensure centers is always an array
      const safeCenters = Array.isArray(centers) ? centers : [];
      setCenterList(safeCenters);

      // Only proceed to next step if we successfully got centers
      setCurrentStep(2);
    } catch (error) {
      console.error("Error loading centers:", error);
      setError("Failed to load medical centers");
      setCenterList([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleConsultationTypeSelect = (type) => {
    if (!type) return;

    setSelectedConsultationType(type);

    if (type === "online") {
      // Skip location selection for online only
      // handleComplete(type, null);
      setIsOnlineSelected(true);
    } else {
      // For in-person or both, check if we have centers
      if (centerList && centerList.length > 0) {
        setCurrentStep(3);
      } else {
        setError("No medical centers available for in-person consultation");
        // You might want to show a toast or modal here
        console.error("No centers available for in-person consultation");
      }
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleComplete = async (
    consultationType = selectedConsultationType,
    location = selectedLocation
  ) => {
    if (!selectedProject || !consultationType) {
      setError("Missing required information");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await dispatch(
        updateavailibility(
          profileId,
          selectedProject.clinic_Id,
          location?.centerID,
          consultationType
        )
      );

      const updatedUserData = {
        ...userData,
        centerIds: response?.centers || [],
        clinicId: selectedProject.clinic_Id,
        centerId: location?.centerID,
        availability: {
          context: consultationType,
          option: location,
        },
        availablityStatus: consultationType,
      };

      localStorage.setItem("userData", JSON.stringify(updatedUserData));
      localStorage.setItem("selectedProject", JSON.stringify(selectedProject));
      dispatch({ type: UPDATE_USER, payload: updatedUserData });

      history.push(`/dashboard?projectId=${selectedProject.clinic_Id}`);
    } catch (error) {
      console.error("Error updating availability:", error);
      setError("Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep === 3) {
      setCurrentStep(2);
      setSelectedLocation(null);
    } else if (currentStep === 2) {
      setCurrentStep(1);
      setSelectedConsultationType(null);
      setCenterList([]);
    }
  };

  const handleStageClick = (stepNumber) => {
    setError(null);
    if (stepNumber < currentStep) {
      if (stepNumber === 1) {
        setCurrentStep(1);
        setSelectedConsultationType(null);
        setCenterList([]);
        setSelectedLocation(null);
      } else if (stepNumber === 2) {
        setCurrentStep(2);
        setSelectedLocation(null);
      }
    }
  };
  const handleLogout = () => {
    const userDetails = JSON.parse(localStorage.getItem("userData"));
    dispatch(logoutAction(userDetails, history)).then((res) => {
      if (res) {
        dispatch({ type: CLEAR_RPM_ADMIN });
        history.push("/signin");
      }
    });
  };
  const getConsultationTypeLabel = (type) => {
    switch (type) {
      case "medicalCentre":
        return "Medical Centre";
      case "online":
        return "Online";
      case "both":
        return "Both";
      default:
        return type || "Not selected";
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.optionalItem}>
          <span>
            <SvgIcons.DigiSetuFullIcon />
          </span>
        </div>
        {/* <button onClick={handleLogout} className={styles.logoutButton}>
          Logout
        </button> */}
        <button onClick={handleLogout} className={styles.logoutButton}>
          <span>⎋</span> {/* or use a logout icon from your icon library */}
          Logout
        </button>
      </div>
      <div className={styles.wrapper}>
        {/* Left Sidebar - Stages */}
        <div className={styles.stagesSidebar}>
          <div className={styles.stagesList}>
            <div
              className={`${styles.stageItem} ${currentStep === 1 ? styles.active : ""} ${currentStep > 1 ? styles.completed : ""}`}
              onClick={() => handleStageClick(1)}
            >
              <div className={styles.stageNumber}>1</div>
              <div className={styles.stageLabel}>Choose Project</div>
              <p className={styles.stageDescription}>
                Select your company project
              </p>
            </div>
            <div
              className={`${styles.stageItem} ${currentStep === 2 ? styles.active : ""} ${currentStep > 2 ? styles.completed : ""}`}
              onClick={() => handleStageClick(2)}
            >
              <div className={styles.stageNumber}>2</div>
              <div className={styles.stageLabel}>Consultation Type</div>
              <p className={styles.stageDescription}>
                Choose how to meet patients
              </p>
            </div>
            <div
              className={`${styles.stageItem} ${currentStep === 3 ? styles.active : ""}`}
              onClick={() => handleStageClick(3)}
            >
              <div className={styles.stageNumber}>3</div>
              <div className={styles.stageLabel}>Select Location</div>
              <p className={styles.stageDescription}>
                Pick your medical centre
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Error Display */}
          {error && (
            <div className={styles.errorMessage}>
              <span>⚠️</span> {error}
            </div>
          )}

          {currentStep === 1 && (
            <ProjectSelection
              projects={projectList}
              selectedProject={selectedProject}
              onSelect={handleProjectSelect}
              profileName={profileName}
              loading={loading}
              onBack={handleBack}
            />
          )}

          {currentStep === 2 && (
            <ConsultationType
              project={selectedProject}
              selectedConsultationType={selectedConsultationType}
              onSelect={handleConsultationTypeSelect}
              onBack={handleBack}
              onComplete={() => handleComplete()}
              hasCenters={centerList.length > 0}
              loading={loading}
            />
          )}

          {currentStep === 3 && (
            <LocationSelection
              centers={centerList} // This is now guaranteed to be an array
              selectedLocation={selectedLocation}
              onSelect={handleLocationSelect}
              onComplete={() => handleComplete()}
              onBack={handleBack}
              loading={loading}
            />
          )}
        </div>

        {/* Right Sidebar - Selected Items */}
        <div className={styles.selectedItemsSidebar}>
          <div className={styles.selectedItemsHeader}>
            <h3>Consultation Setup</h3>
          </div>
          {!selectedProject &&
            !selectedConsultationType &&
            !selectedLocation && (
              <div className={styles.infoMessage}>
                <div className={styles.infoIcon}>ℹ️</div>
                <div className={styles.infoContent}>
                  <div className={styles.infoTitle}>Select to Proceed</div>
                  <p className={styles.infoDescription}>
                    No active project selected. Please choose from the available
                    list.
                  </p>
                </div>
              </div>
            )}
          <div className={styles.selectedItemsList}>
            {selectedProject && (
              <div className={styles.selectedItem}>
                <div className={styles.itemLabel}>Project</div>
                <div className={styles.itemValue}>
                  {selectedProject.clinicName}
                </div>
                {selectedProject.description && (
                  <p className={styles.itemDescription}>
                    {selectedProject.description}
                  </p>
                )}
              </div>
            )}

            {selectedConsultationType && (
              <div className={styles.selectedItem}>
                <div className={styles.itemLabel}>Consultation Type</div>
                <div className={styles.itemValue}>
                  {getConsultationTypeLabel(selectedConsultationType)}
                </div>
                <p className={styles.itemDescription}>
                  {selectedConsultationType === "online"
                    ? "Online consultations only"
                    : selectedConsultationType === "medicalCentre"
                      ? "In-person consultations only"
                      : "Both online and in-person"}
                </p>
              </div>
            )}
            {/* Show message when online consultation is selected */}
            {isOnlineSelected && selectedConsultationType === "online" && (
              <div className={styles.infoMessage}>
                <div className={styles.infoIcon}>ℹ️</div>
                <div className={styles.infoContent}>
                  <div className={styles.infoTitle}>
                    Online Consultation Selected
                  </div>
                  <p className={styles.infoDescription}>
                    Location selection is not required for online consultations.
                    You can provide remote consultations from any location.
                  </p>
                </div>
              </div>
            )}
            {selectedLocation && (
              <div className={styles.selectedItem}>
                <div className={styles.itemLabel}>Location</div>
                <div className={styles.itemValue}>
                  {selectedLocation.centerName ||
                    selectedLocation.name ||
                    "Unnamed Center"}
                </div>
                {selectedLocation.address && (
                  <p className={styles.itemDescription}>
                    {selectedLocation.address}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardProject;
